<script setup>

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import { trans } from 'laravel-vue-i18n';
import Slider from "primevue/slider";
import Button from "primevue/button";
import ToggleButton from "primevue/togglebutton";
import ButtonGroup from "primevue/buttongroup";
import FormMockUp from "./FormMockUp.vue";
import FileUploader from "./FileUploader.vue";
import ColorPicker from "primevue/colorpicker";
import InputMask from "primevue/inputmask";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import SelectButton from "primevue/selectbutton";
import Select from "primevue/select";
import ThemePoint from "./settings/ThemePoint.vue";

const previewStyle = computed(() => ({
    backgroundImage: theme.value.background_image_url ? `url(${theme.value.background_image_url})` : '',
    backgroundColor: `#${theme.value.background_color}`,
}));

const props = defineProps({
    forms: Object,
    thin: Boolean,
    customForm: Array
})

const sourceTheme = defineModel('theme')
const settings = defineModel('settings')

const theme = computed({
    get() {
        if (sourceTheme.value.darkMode) {
            return {
                color: sourceTheme.value.color,
                borderless: sourceTheme.value.borderless,
                transparent: sourceTheme.value.transparent,
                darkMode: sourceTheme.value.darkMode,
                darkModeLock: sourceTheme.value.darkModeLock,
                background_color: sourceTheme.value.background_color_dark ?? sourceTheme.value.background_color,
                background_image_url: sourceTheme.value.background_image_url_dark ?? sourceTheme.value.background_image_url,
                logo_url: sourceTheme.value.logo_url_dark ?? sourceTheme.value.logo_url,
                panel_color: sourceTheme.value.panel_color_dark ?? sourceTheme.value.panel_color,
                panel_transparency: sourceTheme.value.panel_transparency_dark ?? sourceTheme.value.panel_transparency,
                panel_backdrop_blur: sourceTheme.value.panel_backdrop_blur_dark ?? sourceTheme.value.panel_backdrop_blur,
                fontFamily: sourceTheme.value.fontFamily,
                rows: sourceTheme.value.rows,
                columns: sourceTheme.value.columns,
                roundness: sourceTheme.value.roundness,
            }
        }
        return {
            color: sourceTheme.value.color,
            borderless: sourceTheme.value.borderless,
            transparent: sourceTheme.value.transparent,
            darkMode: sourceTheme.value.darkMode,
            darkModeLock: sourceTheme.value.darkModeLock,
            background_color: sourceTheme.value.background_color,
            background_image_url: sourceTheme.value.background_image_url,
            logo_url: sourceTheme.value.logo_url,
            panel_color: sourceTheme.value.panel_color,
            panel_transparency: sourceTheme.value.panel_transparency,
            panel_backdrop_blur: sourceTheme.value.panel_backdrop_blur,
            fontFamily: sourceTheme.value.fontFamily,
            rows: sourceTheme.value.rows,
            columns: sourceTheme.value.columns,
            roundness: sourceTheme.value.roundness,
        }
    },
    set(val) {
        Object.assign(sourceTheme.value, val);
    }
})

const availableColors = [
    'gray',
    'red',
    'orange',
    'yellow',
    'green',
    'teal',
    'blue',
    'indigo',
    'purple',
    'pink',
];

const transparent = [
    {
        value: false,
        label: trans('forms.theme_filled_bg'),
        icon: 'pi pi-circle-fill'
    },
    {
        value: true,
        label: trans('forms.theme_empty_bg'),
        icon: 'pi pi-circle'
    }
];
const titlePosition = [
    {
        value: 'left',
        icon: 'pi pi-align-left',
        label: trans('forms.theme_position_left')
    },
    {
        value: 'center',
        icon: 'pi pi-align-center',
        label: trans('forms.theme_position_center')
    }
];
const days = [
    {
        label: trans('common.dow_long.mon'),
        value: 2
    },
    {
        label: trans('common.dow_long.tue'),
        value: 3
    },
    {
        label: trans('common.dow_long.wed'),
        value: 4
    },
    {
        label: trans('common.dow_long.thu'),
        value: 5
    },
    {
        label: trans('common.dow_long.fri'),
        value: 6
    },
    {
        label: trans('common.dow_long.sat'),
        value: 7
    },
    {
        label: trans('common.dow_long.sun'),
        value: 1
    },
];
const setSetting = (key, value) => {
    const darkModeable = [
        'background_color', 'background_image_url', 'logo_url', 'panel_color', 'panel_transparency', 'panel_backdrop_blur'
    ]
    if (darkModeable.includes(key) && sourceTheme.value.darkMode)
        key += '_dark';

    sourceTheme.value[key] = value;
}

watch(() => sourceTheme.value.darkMode, (val) => {
    if (val) {
        document.querySelector('html')?.classList.add('dark');
    } else {
        document.querySelector('html')?.classList.remove('dark');
    }
}, {immediate: true})

onUnmounted(() => {
    if (!window.matchMedia) {
        return;
    }
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    if (query.matches) {
        document.querySelector('html')?.classList.add('dark');
    } else {
        document.querySelector('html')?.classList.remove('dark');
    }
})
const expanded = ref(false);
</script>

<template>
    <div>
        <div class=" dark:border-gray-800 overflow-hidden flex flex-col" :class="thin ? '' : 'border my-n2 rounded-lg'">
            <div class="dark:border-gray-800 md:p-4 bg-center bg-cover flex max-md:flex-col" :class="thin ? 'h-dvh' : ''" :style="previewStyle">
                <div class="max-md:order-2 md:flex-col md:gap-2 flex max-md:overflow-x-scroll">
                    <theme-point :title="$t('forms.theme_accent_color')" icon="pi pi-palette">
                        <div class="grid grid-cols-5 gap-y-2 gap-x-1 justify-center">
                            <div v-for="color in availableColors"
                                 class="border dark:border-gray-800 rounded cursor-pointer opacity-90 hover:opacity-70 transition-all flex items-center justify-center text-white p-4"
                                 @click="setSetting('color', color)"
                                 style="width: 30px; height: 30px"
                                 :class="`bg-${color}-500 ${theme.color === color ? 'opacity-100 border-2 shadow':''}`">
                            </div>
                        </div>
                    </theme-point>
                    <theme-point icon="pi pi-stop" :title="$t('forms.theme_background')">
                        <div class="gap-2 justify-center">
                            <file-uploader v-if="theme.darkMode" v-model:src="sourceTheme.background_image_url_dark"></file-uploader>
                            <file-uploader v-else v-model:src="sourceTheme.background_image_url"></file-uploader>
                            <div v-if="theme.darkMode" class="flex gap-2 items-center mt-3">
                                <div class="border rounded-lg overflow-hidden">
                                    <ColorPicker v-model="sourceTheme.background_color_dark" format="hex" inline></ColorPicker>
                                </div>
                                <div style="width: 150px">
                                    <IconField>
                                        <InputIcon class="pi pi-hashtag"></InputIcon>
                                        <InputMask v-model="sourceTheme.background_color_dark" mask="******" fluid class="w-full"></InputMask>
                                    </IconField>
                                </div>
                            </div>
                            <div v-else class="flex gap-2 items-center mt-3">
                                <div class="border rounded-lg overflow-hidden">
                                    <ColorPicker v-model="sourceTheme.background_color" format="hex"></ColorPicker>
                                </div>
                                <div style="width: 150px">
                                    <IconField>
                                        <InputIcon class="pi pi-hashtag"></InputIcon>
                                        <InputMask v-model="sourceTheme.background_color" mask="******" fluid class="w-full"></InputMask>
                                    </IconField>
                                </div>
                            </div>
                        </div>
                    </theme-point>

                    <theme-point title="Logo" icon="pi pi-image">
                        <file-uploader v-if="theme.darkMode" v-model:src="sourceTheme.logo_url_dark"></file-uploader>
                        <file-uploader v-else v-model:src="sourceTheme.logo_url"></file-uploader>
                    </theme-point>

                    <theme-point :title="$t('forms.theme_panel')" icon="pi pi-clone">
                        <template v-if="theme.darkMode">
                            <div class="w-full">
                                <Slider v-model="sourceTheme.panel_transparency_dark" :step="25"></Slider>
                            </div>
                            <ToggleButton v-model="sourceTheme.panel_backdrop_blur_dark"
                                          :disabled="sourceTheme.panel_transparency_dark === 100"
                                          :on-label="$t('forms.theme_blurred')" :off-label="$t('forms.theme_not_blurred')"></ToggleButton>
                            <div class="flex gap-2 items-center mt-3">
                                <div class="border rounded-lg overflow-hidden">
                                    <ColorPicker v-model="sourceTheme.panel_color_dark" format="hex"></ColorPicker>
                                </div>
                                <div style="width: 150px">
                                    <IconField>
                                        <InputIcon class="pi pi-hashtag"></InputIcon>
                                        <InputMask v-model="sourceTheme.panel_color_dark" mask="******" fluid class="w-full"></InputMask>
                                    </IconField>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="w-full">
                                <Slider v-model="sourceTheme.panel_transparency" :step="25"></Slider>
                            </div>
                            <ToggleButton v-model="sourceTheme.panel_backdrop_blur"
                                          :disabled="sourceTheme.panel_transparency === 100"
                                          :on-label="$t('forms.theme_blurred')" :off-label="$t('forms.theme_not_blurred')"></ToggleButton>
                            <div class="flex gap-2 items-center mt-3">
                                <div class="border rounded-lg overflow-hidden">
                                    <ColorPicker v-model="sourceTheme.panel_color" format="hex"></ColorPicker>
                                </div>
                                <div style="width: 150px">
                                    <IconField>
                                        <InputIcon class="pi pi-hashtag"></InputIcon>
                                        <InputMask v-model="sourceTheme.panel_color" mask="******" fluid class="w-full"></InputMask>
                                    </IconField>
                                </div>
                            </div>
                        </template>
                    </theme-point>
                </div>
                <div class="max-md:order-1 flex items-center flex-col justify-center relative grow">
                    <div class="flex items-center justify-center pt-12 " v-if="theme.logo_url">
                        <img v-if="theme.logo_url" :src="theme.logo_url" width="150" height="150" alt=""/>
                    </div>
                    <div class="absolute top-3 right-6 ">
                        <ButtonGroup>
                            <Button rounded :disabled="theme.darkModeLock"
                                    :severity="theme.darkMode ? 'contrast' : 'secondary'"
                                    :icon="theme.darkMode ? 'pi pi-moon' : 'pi pi-sun'"
                                    :label="theme.darkMode ? $t('forms.theme_mode_dark') : $t('forms.theme_mode_light')"
                                    @click="sourceTheme.darkMode = !sourceTheme.darkMode"></Button>
                            <Button rounded :severity="theme.darkMode ? 'secondary' : 'contrast'"
                                    :icon="theme.darkModeLock ? 'pi pi-lock' : 'pi pi-lock-open'"
                                    @click="sourceTheme.darkModeLock = !sourceTheme.darkModeLock"></Button>
                        </ButtonGroup>
                    </div>
                    <div class="transform-gpu scale-75 -my-12 pointer-events-none">
                        <form-mock-up :forms="forms" :settings="settings" :custom-form="customForm"
                                      :theme="theme"></form-mock-up>
                    </div>
                </div>
                <div class="max-md:order-2 md:flex-col md:gap-2 flex max-md:overflow-x-auto">
                    <theme-point :title="$t('forms.theme_roundness')" icon="pi pi-circle">
                        <div class="grid grid-cols-4 gap-2 justify-center">
                            <Button v-for="n in 4" :key="n"
                                    class=" dark:bg-gray-900"
                                    :severity="theme.roundness === n - 1 ? 'contrast' : 'secondary'"
                                    @click="setSetting('roundness', n-1)">
                                <template #icon>
                                    <div class="border-2 p-2"
                                         :class="{'rounded': n > 1, 'rounded-lg': n === 3, 'rounded-xl': n === 4}"></div>
                                </template>
                            </Button>
                        </div>
                    </theme-point>
                    <theme-point :title="$t('forms.theme_calendar_bg')" icon="pi pi-calendar">
                        <ToggleButton v-model="sourceTheme.borderless" :on-icon="transparent[1]?.icon"
                                      :off-icon="transparent[0]?.icon" :on-label="transparent[1]?.label"
                                      :off-label="transparent[0]?.label"></ToggleButton>
                    </theme-point>
                    <theme-point :title="$t('forms.theme_month_position')" icon="pi pi-align-center">
                        <SelectButton v-model="settings.titlePosition" :options="titlePosition"
                                      optionLabel="value" optionValue="value" dataKey="value" aria-labelledby="custom">
                            <template #option="slotProps">
                                <i :class="slotProps.option.icon"></i> {{ slotProps.option.label }}
                            </template>
                        </SelectButton>
                    </theme-point>
                    <theme-point :title="$t('forms.theme_first_day')" icon="pi pi-sun">
                        <Select v-model="settings.firstDayOfWeek" class="w-56" :options="days" optionLabel="label" optionValue="value"></Select>
                    </theme-point>
                    <theme-point :title="$t('forms.theme_week_numbers')" icon="pi pi-list-check">
                        <ToggleButton v-model="settings.showWeekNumbers" :on-label="$t('common.yes')" :off-label="$t('common.no')"></ToggleButton>
                    </theme-point>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
